import React, { useState, useEffect, useMemo } from 'react';
import { Negociacao, UnidadeConsumidora, AnaliseCadastral } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { Edit2, Save, X, Activity, CheckCircle2, Zap, ArrowRight, AlertTriangle, Send, XCircle, FileWarning } from 'lucide-react';
import UcAnalysisModal from '../components/UcAnalysisModal';
import { consumoMedioUc } from '../../../../src/lib/analiseTecnicaConsumo';

/** Consumo médio calculado pelo analista — regra única em src/lib/analiseTecnicaConsumo.ts */
const getUcCalcAvg = (uc: UnidadeConsumidora): number => {
    const dte = uc.dados_tecnicos_equatorial as any;
    return consumoMedioUc({
        historico: dte?.historyTable,
        tipoLigacao: dte?.tipoFornecimento || uc.tipo_ligacao,
        possuiGD: dte?.checks?.possuiGD,
    });
};

/** Get UC status from dados_tecnicos_equatorial */
const getUcStatus = (uc: UnidadeConsumidora): 'Aprovada' | 'Reprovada' | 'Pendente' => {
    const dte = uc.dados_tecnicos_equatorial as any;
    if (!dte) return 'Pendente';
    if (dte.status === 'Aprovada') return 'Aprovada';
    if (dte.status === 'Reprovada') return 'Reprovada';
    return 'Pendente';
};

interface Step2Props {
    negociacao: Negociacao;
    onChange: (data: Partial<Negociacao>) => void;
    analiseData: Partial<AnaliseCadastral>;
    onAnaliseChange: (data: Partial<AnaliseCadastral>) => void;
}

export default function Step2RelacaoUCs({ negociacao, onChange, analiseData, onAnaliseChange }: Step2Props) {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [analyzingUc, setAnalyzingUc] = useState<UnidadeConsumidora | null>(null);
    const [saving, setSaving] = useState(false);
    const [pendenciaMsg, setPendenciaMsg] = useState('');
    const [sendingPendencia, setSendingPendencia] = useState(false);

    const ucs = negociacao.unidades_consumidoras as unknown as UnidadeConsumidora[] || [];

    const handleSaveAnalysis = async (id: number, updates: Partial<UnidadeConsumidora>) => {
        try {
            setSaving(true);
            const { error } = await supabase
                .from('unidades_consumidoras')
                .update(updates)
                .eq('id', id);

            if (error) throw error;

            const updatedUcs = ucs.map(u => u.id === id ? { ...u, ...updates } : u);
            onChange({ unidades_consumidoras: updatedUcs as any });

            addToast('Análise da UC salva com sucesso', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'salvar_analise_uc', origem: 'acao' });
            setAnalyzingUc(null);
        } catch (error) {
            console.error('Error saving UC analysis:', error);
            addToast(`Erro: ${(error as any)?.message || 'Erro ao atualizar UC'}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    const analyzedCount = ucs.filter(uc => !!uc.dados_tecnicos_equatorial).length;

    // Global divergence: sum of approved UC calculated averages vs vendor global
    const globalCalcInfo = useMemo(() => {
        let totalApproved = 0;
        let totalAll = 0;
        ucs.forEach(uc => {
            const status = getUcStatus(uc);
            const calcAvg = getUcCalcAvg(uc);
            totalAll += calcAvg;
            if (status !== 'Reprovada') {
                totalApproved += calcAvg;
            }
        });
        return { totalApproved, totalAll };
    }, [ucs]);

    const vendorGlobal = negociacao.consumo_medio_kwh ?? 0;
    const hasGlobalDivergence = analyzedCount > 0 && globalCalcInfo.totalAll > 0 && Math.abs(globalCalcInfo.totalApproved - vendorGlobal) > 50;

    if (ucs.length === 0) {
        return (
            <div className="space-y-6 animate-slide-up">
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                    <div className="p-4 rounded-2xl bg-slate-100 dark:bg-slate-800/60 text-slate-300 dark:text-slate-600">
                        <Zap size={32} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-600 dark:text-slate-300">Nenhuma UC encontrada</h3>
                    <p className="text-sm text-slate-400 dark:text-slate-500 font-medium max-w-sm text-center">
                        Não há unidades consumidoras atreladas a esta negociação.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-slide-up">
            {/* Header */}
            <div className="flex justify-between items-start sm:flex-row flex-col sm:items-center gap-4">
                <div>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <Zap size={20} className="text-secondary" />
                        Relação de UCs
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-1">
                        Realize a análise técnica e cadastral de cada unidade consumidora.
                    </p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-xl">
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                    <span className="text-sm font-bold text-primary">{analyzedCount}/{ucs.length} analisadas</span>
                </div>
            </div>

            {/* Global Divergence Warning */}
            {hasGlobalDivergence && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50 rounded-2xl p-5 flex items-start gap-4 shadow-sm animate-fade-in">
                    <div className="p-2 bg-orange-100 dark:bg-orange-900/40 rounded-xl shrink-0">
                        <FileWarning size={22} className="text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-orange-800 dark:text-orange-400 mb-1">Divergência de Consumo Global Detectada</h4>
                        <p className="text-xs text-orange-700 dark:text-orange-300 font-medium">
                            O vendedor registrou <strong className="font-extrabold text-orange-900 dark:text-orange-200">{vendorGlobal.toLocaleString('pt-BR')} kWh</strong> na negociação.
                            Porém, o somatório calculado das UCs aprovadas totaliza <strong className="font-extrabold text-orange-900 dark:text-orange-200">{globalCalcInfo.totalApproved.toLocaleString('pt-BR')} kWh</strong>.
                            {ucs.some(uc => getUcStatus(uc) === 'Reprovada') && (
                                <> UCs reprovadas ({ucs.filter(uc => getUcStatus(uc) === 'Reprovada').length}) não são contabilizadas no consumo global.</>
                            )}
                        </p>
                    </div>
                </div>
            )}

            {/* Table Card */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead>
                            <tr className="border-b border-slate-100 dark:border-slate-800">
                                <th className="px-5 py-4 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">UC</th>
                                <th className="px-5 py-4 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">Titularidade</th>
                                <th className="px-5 py-4 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">Grupo</th>
                                <th className="px-5 py-4 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">Fase</th>
                                <th className="px-5 py-4 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] text-right">Vendedor (kWh)</th>
                                <th className="px-5 py-4 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] text-right">Analista (kWh)</th>
                                <th className="px-5 py-4 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] text-center">Status</th>
                                <th className="px-5 py-4 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] text-center">Ação</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800/60">
                            {ucs.map((uc, idx) => {
                                const analysisDone = !!uc.dados_tecnicos_equatorial;
                                const ucStatus = getUcStatus(uc);
                                const calcAvg = getUcCalcAvg(uc);
                                const vendorUcConsumo = uc.consumo_medio_kwh ?? 0;
                                const hasDivergence = analysisDone && calcAvg > 0 && Math.abs(calcAvg - vendorUcConsumo) > 50;

                                return (
                                    <tr
                                        key={uc.id}
                                        className={`group hover:bg-primary/[0.02] dark:hover:bg-primary/[0.04] transition-all duration-200 animate-stagger-in ${ucStatus === 'Reprovada' ? 'bg-red-50/30 dark:bg-red-900/5' : ''}`}
                                        style={{ animationDelay: `${idx * 50}ms` }}
                                    >
                                        <td className="px-5 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-2 h-2 rounded-full shrink-0 ${ucStatus === 'Aprovada' ? 'bg-green-400' : ucStatus === 'Reprovada' ? 'bg-red-400' : 'bg-amber-400 animate-pulse'}`}></div>
                                                <div>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-bold text-slate-800 dark:text-slate-100 font-mono text-[13px]">{uc.numero_uc}</span>
                                                        {uc.outro_numero_uc && (
                                                            <span className="font-bold text-slate-500 bg-slate-100 dark:bg-slate-700 dark:text-slate-300 px-1.5 rounded text-[10px]">
                                                                {uc.outro_numero_uc}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {(uc.dados_tecnicos_equatorial as any)?.checks?.possuiGD === true && (
                                                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/40 dark:to-yellow-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-extrabold border border-amber-300/80 dark:border-amber-700/60 shadow-sm ml-1.5 uppercase tracking-wider animate-fade-in">
                                                            <Zap size={10} className="text-amber-500 dark:text-amber-400" />
                                                            GD
                                                        </span>
                                                    )}
                                                    {uc.dados_tecnicos_equatorial?.nParceiro && uc.dados_tecnicos_equatorial.nParceiro !== uc.numero_uc && (
                                                        <span className="block text-[10px] font-medium text-slate-400 mt-0.5">
                                                            Parceiro: {uc.dados_tecnicos_equatorial.nParceiro}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-5 py-4">
                                            <span className="text-slate-600 dark:text-slate-300 font-medium text-[13px]">
                                                {uc.dados_tecnicos_equatorial?.titularidade || uc.titular_fatura || uc.nome_cliente || '\u2014'}
                                            </span>
                                        </td>

                                        <td className="px-5 py-4">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold border border-slate-200/80 dark:border-slate-700/80">
                                                {uc.dados_tecnicos_equatorial?.grupo || uc.grupo_tarifario || '\u2014'}
                                            </span>
                                        </td>

                                        <td className="px-5 py-4">
                                            <span className="text-slate-600 dark:text-slate-300 font-medium text-[13px]">
                                                {uc.dados_tecnicos_equatorial?.tipoFornecimento || uc.tipo_ligacao || '\u2014'}
                                            </span>
                                        </td>

                                        <td className="px-5 py-4 text-right">
                                            <span className={`font-bold tabular-nums ${hasDivergence ? 'text-orange-600 dark:text-orange-400' : 'text-slate-500 dark:text-slate-400'}`}>
                                                {vendorUcConsumo > 0 ? vendorUcConsumo.toLocaleString('pt-BR') : '\u2014'}
                                            </span>
                                        </td>

                                        <td className="px-5 py-4 text-right">
                                            {analysisDone && calcAvg > 0 ? (
                                                <span className={`font-bold tabular-nums ${hasDivergence ? 'text-primary' : 'text-slate-700 dark:text-slate-200'}`}>
                                                    {calcAvg.toLocaleString('pt-BR')}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400 text-xs italic">{'\u2014'}</span>
                                            )}
                                        </td>

                                        <td className="px-5 py-4 text-center">
                                            {ucStatus === 'Aprovada' ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200/80 dark:border-green-800/40 shadow-sm">
                                                    <CheckCircle2 size={12} /> Aprovada
                                                </span>
                                            ) : ucStatus === 'Reprovada' ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200/80 dark:border-red-800/40 shadow-sm">
                                                    <XCircle size={12} /> Reprovada
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-amber-50 dark:bg-amber-900/15 text-amber-600 dark:text-amber-400 border border-amber-200/80 dark:border-amber-800/40">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></div>
                                                    Pendente
                                                </span>
                                            )}
                                        </td>

                                        <td className="px-5 py-4 text-center">
                                            <button
                                                onClick={() => setAnalyzingUc(uc)}
                                                disabled={saving}
                                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.97] cursor-pointer
                                                    ${analysisDone
                                                        ? 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-primary/40 hover:text-primary'
                                                        : 'bg-gradient-to-r from-primary to-primary/90 text-white hover:from-primary/95 hover:to-primary/85 shadow-primary/20'
                                                    }
                                                `}
                                                title={analysisDone ? "Revisar Análise" : "Iniciar Análise"}
                                            >
                                                {analysisDone ? <Edit2 size={14} /> : <Activity size={14} />}
                                                {analysisDone ? 'Revisar' : 'Analisar'}
                                                <ArrowRight size={12} className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Global Summary Footer */}
                {analyzedCount > 0 && (
                    <div className="px-5 py-3 bg-slate-50/80 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-4 text-xs font-bold">
                        <span className="text-slate-500">
                            Vendedor Global: <span className="text-slate-700 dark:text-slate-200">{vendorGlobal.toLocaleString('pt-BR')} kWh</span>
                        </span>
                        <span className="text-slate-500">
                            Analista (UCs aprovadas): <span className="text-primary">{globalCalcInfo.totalApproved.toLocaleString('pt-BR')} kWh</span>
                        </span>
                        {ucs.some(uc => getUcStatus(uc) === 'Reprovada') && (
                            <span className="text-red-500">
                                Reprovadas excluídas: {ucs.filter(uc => getUcStatus(uc) === 'Reprovada').length} UC(s)
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Notificar Vendedor */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden mt-6">
                <div className="p-5">
                    <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/40 rounded-xl">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle size={16} className="text-orange-600" />
                            <span className="text-sm font-bold text-orange-800 dark:text-orange-400">Notificar Vendedor sobre Pendência Documental</span>
                        </div>
                        <textarea
                            value={pendenciaMsg}
                            onChange={(e) => setPendenciaMsg(e.target.value)}
                            rows={2}
                            className="w-full rounded-lg border border-orange-200 dark:border-orange-700/50 bg-white dark:bg-slate-800/60 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none resize-none placeholder:text-orange-300"
                            placeholder="Descreva a pendência que o vendedor precisa resolver em até 24h..."
                        />
                        <button
                            disabled={!pendenciaMsg.trim() || sendingPendencia}
                            onClick={async () => {
                                if (!pendenciaMsg.trim()) return;
                                setSendingPendencia(true);
                                try {
                                    await supabase.from('negociacao_logs').insert({
                                        negociacao_id: negociacao.id,
                                        acao: `[PENDENCIA_DOCUMENTAL] ${pendenciaMsg}`,
                                        detalhes: `Analista ${profile?.full_name || 'Analista'} identificou pendência documental no Step 2 (Relação de UCs).`,
                                        user_id: user?.id
                                    });
                                    const sellerId = negociacao.user_id || negociacao.responsavel_id;
                                    if (sellerId) {
                                        await supabase.from('notifications').insert({
                                            user_id: sellerId,
                                            type: 'pendencia_documental',
                                            title: 'Pendência Documental - Ação Necessária',
                                            content: `Pendência na negociação "${negociacao.cliente_nome}": ${pendenciaMsg}. Prazo: 24h.`,
                                            link: `/crm?negociacaoId=${negociacao.id}`,
                                            read: false
                                        });
                                    }
                                    addToast('Pendência enviada ao vendedor!', 'success');
                                    logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'enviar_pendencia_documental', origem: 'acao' });
                                    setPendenciaMsg('');
                                } catch (err: any) {
                                    addToast('Erro ao enviar: ' + err.message, 'error');
                                } finally {
                                    setSendingPendencia(false);
                                }
                            }}
                            className="mt-2 flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-xs font-bold rounded-lg hover:bg-orange-700 transition-all shadow-sm disabled:opacity-50"
                        >
                            <Send size={14} /> {sendingPendencia ? 'Enviando...' : 'Enviar Pendência ao Vendedor'}
                        </button>
                    </div>
                </div>
            </div>

            {analyzingUc && (
                <UcAnalysisModal
                    isOpen={true}
                    onClose={() => setAnalyzingUc(null)}
                    uc={analyzingUc}
                    onSave={handleSaveAnalysis}
                    analiseData={analiseData}
                    onAnaliseChange={onAnaliseChange}
                    negociacao={negociacao}
                />
            )}
        </div>
    );
}
